import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import VerifyPhone from "@/pages/VerifyPhone";
import { formatPrice, formatHour, hexToRgba } from "@/lib/utils";
import { Calendar, Clock, MapPin, Star, Heart, Users, Copy, Share2, UserCheck, Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { auth } from "@/lib/firebase";
import { updateProfile } from "firebase/auth";

const STATUS_STYLES = {
  confirmed: "border-volt text-volt",
  pending: "border-yellow-400 text-yellow-400",
  cancelled: "border-border text-muted-foreground/80",
  rejected: "border-red-400 text-red-400",
};

const PAYMENT_STYLES = {
  mocked_paid: "text-emerald-400",
  pending_cod: "text-yellow-400",
  paid_at_court: "text-volt",
  pending_online: "text-yellow-400",
};

export default function Profile() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [playStats, setPlayStats] = useState({ sessions_played: 0, sessions_hosted: 0 });
  const [playerRatings, setPlayerRatings] = useState({ count: 0, punctuality_avg: 0, skill_avg: 0, attitude_avg: 0, overall_avg: 0 });
  const [badges, setBadges] = useState([]);
  const [playerProfile, setPlayerProfile] = useState(null);
  const [editingNickname, setEditingNickname] = useState(false);
  const [editNickname, setEditNickname] = useState("");
  const [friendList, setFriendList] = useState([]);
  const [followerList, setFollowerList] = useState([]);
  const fileRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm] = useState({ nickname: "", bio: "", position: "", phone_visibility: "public", favorite_sports: [] });
  const [savingProfile, setSavingProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const avatarUrl = res.data.url;
      await api.patch("/me/player", { avatar: avatarUrl });
      setPlayerProfile({ ...playerProfile, avatar: avatarUrl });
      toast.success(t("player.avatar_updated"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/profile"),
      api.get("/wishlist"),
      api.get("/me/player").catch(() => null),
      api.get("/me/friends").catch(() => null),
      api.get("/me/followers").catch(() => null),
    ])
      .then(([pRes, fRes, playerRes, friendsRes, followersRes]) => {
        setBookings(pRes.data.bookings);
        setReviews(pRes.data.reviews);
        setFavorites(fRes.data);
        setPlayStats(pRes.data.play_stats || { sessions_played: 0, sessions_hosted: 0 });
        setPlayerRatings(pRes.data.ratings || { count: 0, punctuality_avg: 0, skill_avg: 0, attitude_avg: 0, overall_avg: 0 });
        setBadges(pRes.data.badges || []);
        if (playerRes) setPlayerProfile(playerRes.data.player);
        if (friendsRes) setFriendList(friendsRes.data.friends || []);
        if (followersRes) setFollowerList(followersRes.data.followers || []);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">

        {loading ? (
          <div className="text-muted-foreground/80 label-eyebrow animate-pulse">{t("common.loading")}</div>
        ) : (
          <Tabs defaultValue="player" className="w-full">
            <TabsList className="bg-transparent border border-border p-1 rounded-none">
              <TabsTrigger value="player" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-primary-foreground data-[state=active]:font-bold uppercase text-xs tracking-widest">
                <Users className="h-3 w-3 inline-block mr-1" />
                {t("profile.my_player")}
              </TabsTrigger>
              <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-primary-foreground data-[state=active]:font-bold uppercase text-xs tracking-widest">
                {t("profile.booking_history")} ({bookings.length})
              </TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-primary-foreground data-[state=active]:font-bold uppercase text-xs tracking-widest">
                {t("profile.my_reviews")} ({reviews.length})
              </TabsTrigger>
              <TabsTrigger value="favorites" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-primary-foreground data-[state=active]:font-bold uppercase text-xs tracking-widest">
                {t("profile.favorites")} ({favorites.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="player" className="mt-6">
              {playerProfile ? (
                <div className="border border-border/30 bg-card p-6 md:p-8">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                    <div>
                      <div className="label-eyebrow text-volt mb-2">{t("player.profile")}</div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative group">
                          <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                          {playerProfile.avatar ? (
                            <img src={playerProfile.avatar} alt="avatar" className="w-16 h-16 rounded-full object-cover" />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-volt/10 flex items-center justify-center text-2xl font-heading text-volt">
                              {(playerProfile.nickname || user?.name || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                          {uploadingAvatar ? (
                            <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                              <Loader2 className="h-5 w-5 text-white animate-spin" />
                            </div>
                          ) : (
                            <button
                              onClick={() => fileRef.current?.click()}
                              className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            >
                              <Camera className="h-5 w-5 text-white" />
                            </button>
                          )}
                        </div>
                        <div>
                          {editingNickname ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editNickname}
                                onChange={(e) => setEditNickname(e.target.value)}
                                className="border border-border bg-background px-3 py-1 text-sm text-foreground focus:outline-none focus:border-volt"
                                autoFocus
                              />
                              <button
                                onClick={async () => {
                                  try {
                                    await api.patch("/me/player", { nickname: editNickname });
                                    setPlayerProfile({ ...playerProfile, nickname: editNickname });
                                    setEditingNickname(false);
                                    if (auth.currentUser) {
                                      await updateProfile(auth.currentUser, { displayName: editNickname });
                                    }
                                    toast.success(t("player.nickname_updated"));
                                  } catch (e) {
                                    toast.error(formatApiError(e));
                                  }
                                }}
                                className="btn-volt px-3 py-1 text-[10px]"
                              >
                                {t("common.save")}
                              </button>
                              <button onClick={() => setEditingNickname(false)} className="text-muted-foreground/80 text-xs px-2 py-1">
                                {t("common.cancel")}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <h2 className="font-heading text-3xl">{playerProfile.nickname || user?.name}</h2>
                              <button
                                onClick={() => { setEditNickname(playerProfile.nickname || user?.name || ""); setEditingNickname(true); }}
                                className="text-muted-foreground/60 hover:text-foreground text-xs"
                              >
                                {t("common.edit")}
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="font-mono">{playerProfile.player_id}</span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(playerProfile.player_id);
                                toast.success(t("player.player_id_copied"));
                              }}
                              className="text-muted-foreground/60 hover:text-foreground"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                          {user?.phone_verified && user?.phone && (
                            <div className="flex items-center gap-1.5 text-xs text-volt mt-1">
                              <span className="font-mono">{user.phone}</span>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" title={t("phone.verified")} />
                              <button
                                onClick={() => setShowPhoneModal(true)}
                                className="text-muted-foreground/60 hover:text-foreground ml-1"
                              >
                                {t("common.change")}
                              </button>
                            </div>
                          )}
                          {!user?.phone_verified && (
                            <Link to="/verify-phone" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-volt mt-1">
                              {t("phone.verify_title")}
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Link
                        to={`/player/${playerProfile.player_id}`}
                        className="btn-outline px-4 py-2 text-xs"
                      >
                        {t("player.view_public_profile")}
                      </Link>
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/player/${playerProfile.player_id}`;
                          if (navigator.share) {
                            navigator.share({ url });
                          } else {
                            navigator.clipboard.writeText(url);
                            toast.success(t("common.link_copied"));
                          }
                        }}
                        className="btn-outline px-3 py-2 text-xs flex items-center gap-1"
                      >
                        <Share2 className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => {
                          setEditForm({
                            nickname: playerProfile.nickname || user?.name || "",
                            bio: playerProfile.bio || "",
                            position: playerProfile.position || "",
                            phone_visibility: playerProfile.phone_visibility || "public",
                            favorite_sports: playerProfile.favorite_sports || [],
                          });
                          setShowEditProfile(true);
                        }}
                        className="btn-volt px-4 py-2 text-[10px] tracking-widest uppercase"
                      >
                        {t("player.edit_profile")}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    <div className="border border-border/30 p-3 text-center">
                      <div className="font-heading text-xl text-volt">{playerProfile.average_rating || "-"}</div>
                      <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("profile.ratings_title")}</div>
                    </div>
                    <div className="border border-border/30 p-3 text-center">
                      <div className="font-heading text-xl text-volt">{playerProfile.stats?.matches_played || 0}</div>
                      <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("player.matches_played")}</div>
                    </div>
                    <div className="border border-border/30 p-3 text-center">
                      <div className="font-heading text-xl text-volt">{playerProfile.stats?.wins || 0}</div>
                      <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("player.wins")}</div>
                    </div>
                    <div className="border border-border/30 p-3 text-center">
                      <div className="font-heading text-xl text-volt">{playerProfile.stats?.losses || 0}</div>
                      <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("player.losses")}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <h3 className="text-xs tracking-widest uppercase font-semibold text-muted-foreground mb-3">{t("player.my_teams")} ({(playerProfile.my_teams || []).length})</h3>
                      {playerProfile.my_teams && playerProfile.my_teams.length > 0 ? (
                        <div className="space-y-2">
                          {playerProfile.my_teams.map((team) => (
                            <Link key={team.id} to={`/teams/${team.id}`} className="block border border-border/30 p-3 hover:border-border/60 transition-colors">
                              <span className="font-heading text-lg tracking-tight hover:text-volt">{team.name}</span>
                              <span className="text-xs text-muted-foreground/80 ml-2">{team.sport}</span>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground/60 text-sm">{t("player.no_teams")}</p>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xs tracking-widest uppercase font-semibold text-muted-foreground mb-3">{t("player.following_list")} ({playerProfile.following_count || 0})</h3>
                      {friendList.length > 0 ? (
                        <div className="space-y-2">
                          {friendList.map((f) => (
                            <Link key={f.player_id} to={`/player/${f.player_id}`} className="flex items-center gap-3 border border-border/30 p-3 hover:border-border/60 transition-colors">
                              <div className="w-8 h-8 rounded-full bg-volt/10 flex items-center justify-center text-xs font-heading text-volt">
                                {(f.nickname || "?").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="text-sm hover:text-volt">{f.nickname}</span>
                                <span className="text-xs text-muted-foreground/60 ml-2 font-mono">{f.player_id}</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground/60 text-sm">{t("player.no_friends")}</p>
                      )}
                    </div>
                    <div>
                      <h3 className="text-xs tracking-widest uppercase font-semibold text-muted-foreground mb-3">{t("player.followers_list")} ({playerProfile.followers_count || 0})</h3>
                      {followerList.length > 0 ? (
                        <div className="space-y-2">
                          {followerList.map((f) => (
                            <Link key={f.player_id} to={`/player/${f.player_id}`} className="flex items-center gap-3 border border-border/30 p-3 hover:border-border/60 transition-colors">
                              <div className="w-8 h-8 rounded-full bg-volt/10 flex items-center justify-center text-xs font-heading text-volt">
                                {(f.nickname || "?").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="text-sm hover:text-volt">{f.nickname}</span>
                                <span className="text-xs text-muted-foreground/60 ml-2 font-mono">{f.player_id}</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground/60 text-sm">{t("player.no_followers")}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-border p-16 text-center">
                  <p className="font-heading text-3xl text-muted-foreground/80">{t("player.no_profile")}</p>
                </div>
              )}
              {showEditProfile && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-20 px-4" onClick={() => setShowEditProfile(false)}>
                  <div className="bg-card border border-border w-full max-w-lg p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setShowEditProfile(false)} className="absolute top-4 right-4 text-muted-foreground/60 hover:text-foreground">
                      <X className="h-4 w-4" />
                    </button>
                    <h2 className="font-heading text-2xl tracking-tight mb-6">{t("player.edit_profile")}</h2>

                    {/* Nickname */}
                    <div className="mb-4">
                      <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">{t("player.nickname")}</label>
                      <input value={editForm.nickname} onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })} className="w-full border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-volt" />
                    </div>

                    {/* Bio */}
                    <div className="mb-4">
                      <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">{t("player.bio")}</label>
                      <textarea value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} rows={3} className="w-full border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-volt resize-none" />
                    </div>

                    {/* Favourite Sports */}
                    <div className="mb-4">
                      <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">{t("player.favorite_sports")}</label>
                      <div className="flex flex-wrap gap-2">
                        {["football", "basketball", "tennis", "padel", "pickleball", "volleyball", "badminton", "squash"].map((sport) => {
                          const selected = editForm.favorite_sports.includes(sport);
                          return (
                            <button
                              key={sport}
                              type="button"
                              onClick={() => setEditForm({
                                ...editForm,
                                favorite_sports: selected ? editForm.favorite_sports.filter((s) => s !== sport) : [...editForm.favorite_sports, sport]
                              })}
                              className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                                selected ? "bg-volt text-primary-foreground border-volt" : "border-border text-muted-foreground/80 hover:border-muted-foreground/50"
                              }`}
                            >
                              {t("home.sports." + sport)}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Position */}
                    <div className="mb-4">
                      <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">{t("player.position")} <span className="text-muted-foreground/50">({t("common.optional")})</span></label>
                      <input value={editForm.position} onChange={(e) => setEditForm({ ...editForm, position: e.target.value })} placeholder="e.g. Forward, Goalkeeper" className="w-full border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-volt" />
                    </div>

                    {/* Phone Visibility */}
                    <div className="mb-4">
                      <label className="text-[10px] tracking-widest uppercase font-semibold text-muted-foreground mb-1.5 block">{t("player.phone_visibility")}</label>
                      <select value={editForm.phone_visibility} onChange={(e) => setEditForm({ ...editForm, phone_visibility: e.target.value })} className="w-full border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-volt">
                        <option value="public">{t("player.public")}</option>
                        <option value="friends_only">{t("player.friends_only")}</option>
                        <option value="private">{t("player.private")}</option>
                      </select>
                    </div>



                    <button
                      onClick={async () => {
                        setSavingProfile(true);
                        try {
                          const newNickname = editForm.nickname?.trim();
                          await api.patch("/me/player", editForm);
                          setPlayerProfile({ ...playerProfile, ...editForm });
                          setShowEditProfile(false);
                          if (newNickname && auth.currentUser) {
                            await updateProfile(auth.currentUser, { displayName: newNickname });
                          }
                          toast.success(t("player.nickname_updated"));
                        } catch (e) {
                          toast.error(formatApiError(e.response?.data?.detail));
                        } finally {
                          setSavingProfile(false);
                        }
                      }}
                      disabled={savingProfile}
                      className="btn-volt w-full px-6 py-3 text-xs tracking-widest uppercase disabled:opacity-50"
                    >
                      {savingProfile ? t("common.saving") || "Saving..." : t("common.save")}
                    </button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="bookings" className="mt-6">
              {bookings.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center">
                  <p className="font-heading text-3xl text-muted-foreground/80">{t("bookings.none")}</p>
                  <Link to="/search" className="btn-volt inline-block mt-4 px-5 py-2 text-xs">
                    {t("bookings.find_court")}
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((b) => {
                    const payLabel = t(`bookings.payment.${b.payment_status}`, b.payment_status);
                    return (
                      <Link key={b.id} to={`/bookings/${b.id}`} className="block border border-border/30 bg-card p-5 hover:border-border/60 transition-colors">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <span className="font-heading text-2xl tracking-tight hover:text-volt">
                              {b.court_name}
                            </span>
                            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground/80 mt-2">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> {b.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatHour(b.start_hour)} – {formatHour(b.end_hour)}
                              </span>
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="font-heading text-2xl text-volt">{formatPrice(b.total_price, b.currency)}</div>
                            <div className="flex items-center gap-2 mt-1 justify-end">
                              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${STATUS_STYLES[b.status] || "border-border text-muted-foreground/80"}`}>
                                {b.status}
                              </span>
                              <span className={`text-[10px] uppercase tracking-widest ${PAYMENT_STYLES[b.payment_status] || "text-muted-foreground/80"}`}>
                                {payLabel}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="reviews" className="mt-6">
              {reviews.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center">
                  <p className="font-heading text-3xl text-muted-foreground/80">{t("profile.no_reviews")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map((r) => (
                    <div key={r.id} className="border border-border/30 bg-card p-5">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <Link to={`/courts/${r.court_id}`} className="font-heading text-xl tracking-tight hover:text-volt">
                            {r.court_name || r.court_id}
                          </Link>
                          <div className="flex items-center gap-1 mt-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-volt text-volt" : "text-muted-foreground/40"}`} />
                            ))}
                          </div>
                        </div>
                        <div className="text-[10px] text-muted-foreground/80">{r.created_at}</div>
                      </div>
                      {r.comment && <p className="text-muted-foreground/80 text-sm mt-3">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="favorites" className="mt-6">
              {favorites.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center">
                  <p className="font-heading text-3xl text-muted-foreground/80">{t("profile.no_favorites")}</p>
                  <Link to="/search" className="btn-volt inline-block mt-4 px-5 py-2 text-xs">
                    {t("bookings.find_court")}
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {favorites.map((fav) => {
                    const c = fav.court;
                    if (!c) return null;
                    return (
                      <Link key={fav.id} to={`/courts/${c.id}`} className="block border border-border/30 bg-card p-5 hover:border-border/60 transition-colors">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <span className="font-heading text-2xl tracking-tight hover:text-volt">{c.name}</span>
                            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground/80 mt-2">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {c.city}
                              </span>
                              <span className="text-[10px] uppercase tracking-widest text-muted-foreground/80">{c.sport}</span>
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="font-heading text-2xl text-volt">{formatPrice((c.slot_pricing || {})[String(c.default_slot_duration || c.slot_duration || 60)] ?? c.price_per_slot, c.currency)}</div>
                            <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80 mt-1">
                              {c.default_slot_duration && c.default_slot_duration !== 60 ? t("court.per_min", { n: c.default_slot_duration }) : t("court.per_hour")}
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {showPhoneModal && (
        <VerifyPhone
          variant="modal"
          onVerified={() => {
            setShowPhoneModal(false);
            window.location.reload();
          }}
          onClose={() => setShowPhoneModal(false)}
        />
      )}
    </div>
  );
}
